import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Input } from './ui/input';

// Academy participants (juniors or adult members) of ONE lesson — remove / add per lesson.
export const AcademyJuniorChips = ({ booking, API, L, onChange, onOpenJunior }) => {
  const isAdult = booking.academy_category === 'adult' || (booking.academy_member_ids || []).length > 0;
  const idsKey = isAdult ? 'academy_member_ids' : 'academy_junior_ids';
  const namesKey = isAdult ? 'academy_member_names' : 'academy_junior_names';
  const names = booking[namesKey] || [];
  const ids = booking[idsKey] || [];
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (!adding || q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        if (isAdult) {
          const { data } = await API.get(`/users/search?q=${encodeURIComponent(q)}&limit=8`);
          setResults((data || []).map((u) => ({ id: u.id, name: u.name, sub: u.email })));
        } else {
          const { data } = await API.get('/academy/juniors');
          const ql = q.toLowerCase();
          setResults((data || []).filter((j) => j.is_active !== false && (j.name || '').toLowerCase().includes(ql)).slice(0, 8).map((j) => ({ id: j.id, name: j.name })));
        }
      } catch { setResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, adding, isAdult, API]);

  const apply = (data) => onChange({ [idsKey]: data.ids, [namesKey]: data.names, participants: data.participants, amount: data.amount, paid_participants: data.paid_participants });

  const remove = async (n, i) => {
    if (!window.confirm(L(`Remove "${n}" from this one lesson?`, `"${n}" von dieser einen Lektion entfernen?`))) return;
    try {
      const { data } = await API.post(`/bookings/${booking.id}/remove-participant`, ids[i] ? { junior_id: ids[i] } : { junior_name: n });
      apply(data);
      toast.success(L(`${n} removed from this lesson`, `${n} aus dieser Lektion entfernt`));
    } catch (e) { toast.error(e.response?.data?.detail || L('Error', 'Fehler')); }
  };

  const add = async (r) => {
    try {
      const { data } = await API.post(`/bookings/${booking.id}/add-participant`, { person_id: r.id });
      apply(data);
      toast.success(L(`${r.name} added to this lesson`, `${r.name} zu dieser Lektion hinzugefügt`));
      setQ(''); setResults([]); setAdding(false);
    } catch (e) { toast.error(e.response?.data?.detail || L('Error', 'Fehler')); }
  };

  return (
    <div className="border-t border-slate-200 pt-3 space-y-2" data-testid="academy-info-block">
      <div className="flex items-center gap-2">
        <span className="px-1.5 py-0.5 rounded text-[10px] font-extrabold tracking-wider bg-emerald-800 text-white">ACADEMY</span>
        {booking.academy_group_name && <span className="text-sm font-semibold text-emerald-800 truncate">{booking.academy_group_name}</span>}
        <span className="text-xs text-slate-500 ml-auto shrink-0" data-testid="academy-junior-count">{names.length} {L('participants', 'Teilnehmer')}</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {names.map((n, i) => (
          <span key={`${ids[i] || n}-${i}`} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold rounded-full" data-testid={`academy-junior-${i}`}>
            {!isAdult && ids[i] && onOpenJunior ? (
              <button onClick={() => onOpenJunior(ids[i])} className="hover:underline" title={L('Open junior profile', 'Schüler-Profil öffnen')}>{n}</button>
            ) : <span>{n}</span>}
            <button onClick={() => remove(n, i)}
              className="ml-0.5 w-4 h-4 flex items-center justify-center rounded-full bg-red-100 hover:bg-red-200 text-red-600 transition-colors"
              title={L('Excuse for this lesson', 'Für diese Lektion abmelden')} data-testid={`remove-junior-${i}`}>×</button>
          </span>
        ))}
        <button onClick={() => setAdding((v) => !v)} data-testid="academy-add-participant-btn"
          className="px-2 py-0.5 text-xs font-semibold rounded-full border border-dashed border-emerald-400 text-emerald-700 hover:bg-emerald-50 transition-colors">
          + {L('Add', 'Hinzufügen')}
        </button>
      </div>
      {adding && (
        <div className="space-y-1" data-testid="academy-add-participant-panel">
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} className="h-8 text-sm"
            placeholder={isAdult ? L('Search customer…', 'Kunde suchen…') : L('Search junior…', 'Junior suchen…')} data-testid="academy-add-search" />
          {results.filter((r) => !ids.includes(r.id)).map((r) => (
            <button key={r.id} onClick={() => add(r)} data-testid={`academy-add-result-${r.id}`}
              className="w-full text-left px-2 py-1.5 rounded-md hover:bg-emerald-50 text-sm">
              <span className="font-semibold text-slate-900">{r.name}</span>
              {r.sub && <span className="text-xs text-slate-500 ml-2">{r.sub}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
